import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Lock, Play, CheckCircle, Clock, Calendar } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useTrainingModules } from '../hooks/useTrainingModules';
import { useTrainingAssignments } from '../hooks/useTrainingAssignments';
import { TrainingModulePlayer } from './TrainingModulePlayer';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { ModuleWithProgress } from '../types';

export const SellerTrainingView: React.FC = () => {
  const [selectedModule, setSelectedModule] = useState<ModuleWithProgress | null>(null);
  
  const { data: currentUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { modules } = useTrainingModules();
  const { assignments } = useTrainingAssignments(currentUser?.id);

  // Combine modules with their assignments
  const modulesWithProgress: ModuleWithProgress[] = modules
    .filter(m => assignments.some(a => a.module_id === m.id))
    .map(module => ({
      ...module,
      assignment: assignments.find(a => a.module_id === module.id),
    }))
    .sort((a, b) => a.order_position - b.order_position);

  const completedCount = modulesWithProgress.filter(m => m.assignment?.status === 'completed').length;
  const totalCount = modulesWithProgress.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const handleModuleClick = async (module: ModuleWithProgress) => {
    if (module.assignment?.status === 'locked') return;
    
    // Mark as in_progress if pending
    if (module.assignment?.status === 'pending') {
      await supabase
        .from('training_assignments')
        .update({ 
          status: 'in_progress',
          started_at: new Date().toISOString(),
        })
        .eq('id', module.assignment.id);
    }
    
    setSelectedModule(module);
  };

  if (selectedModule) {
    return (
      <TrainingModulePlayer
        module={selectedModule}
        assignment={selectedModule.assignment!}
        onBack={() => setSelectedModule(null)}
      />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 backdrop-blur-xl rounded-2xl p-6 border border-emerald-500/20"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
          <GraduationCap className="w-6 h-6 text-emerald-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-semibold text-lg">Mi Entrenamiento</h3>
          <p className="text-emerald-400/80 text-sm">
            {completedCount} de {totalCount} módulos completados
          </p>
        </div>
        <div className="text-right">
          <span className="text-2xl font-bold text-emerald-400">{progressPercentage.toFixed(0)}%</span>
        </div>
      </div>

      <Progress value={progressPercentage} className="h-2 mb-6" />

      {/* Modules List */}
      {modulesWithProgress.length === 0 ? (
        <div className="text-center py-12">
          <GraduationCap className="w-16 h-16 mx-auto mb-3 text-white/20" />
          <p className="text-white/50 font-medium">Sin módulos asignados</p>
          <p className="text-white/30 text-sm">Tu administrador te asignará cursos pronto</p>
        </div>
      ) : (
        <div className="space-y-3">
          {modulesWithProgress.map((module, index) => {
            const status = module.assignment?.status || 'locked';
            const isLocked = status === 'locked';
            const isCompleted = status === 'completed';
            const isAvailable = status === 'pending' || status === 'in_progress';

            return (
              <motion.button
                key={module.id}
                onClick={() => handleModuleClick(module)}
                disabled={isLocked}
                whileHover={!isLocked ? { scale: 1.01 } : undefined}
                whileTap={!isLocked ? { scale: 0.99 } : undefined}
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all text-left ${
                  isLocked 
                    ? 'bg-white/5 opacity-50 cursor-not-allowed' 
                    : isCompleted
                    ? 'bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20'
                    : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20'
                }`}
              >
                {/* Status Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                  isLocked 
                    ? 'bg-white/5' 
                    : isCompleted 
                    ? 'bg-emerald-500/20' 
                    : 'bg-blue-500/20'
                }`}>
                  {isLocked ? (
                    <Lock className="w-5 h-5 text-white/30" />
                  ) : isCompleted ? (
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                  ) : status === 'in_progress' ? (
                    <Clock className="w-6 h-6 text-blue-400" />
                  ) : (
                    <Play className="w-6 h-6 text-blue-400" />
                  )}
                </div>

                {/* Module Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white/40 text-sm">Módulo {index + 1}</span>
                    {status === 'in_progress' && (
                      <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded">
                        En progreso
                      </span>
                    )}
                  </div>
                  <h4 className={`font-medium truncate ${isLocked ? 'text-white/40' : 'text-white'}`}>
                    {module.title}
                  </h4>
                  {module.description && (
                    <p className="text-white/50 text-sm truncate">{module.description}</p>
                  )}
                </div>

                {/* Completion Date or Expected Date */}
                <div className="text-right shrink-0">
                  {isCompleted && module.assignment?.completed_at && (
                    <div className="text-emerald-400 text-xs">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      {format(new Date(module.assignment.completed_at), 'dd MMM', { locale: es })}
                    </div>
                  )}
                  {!isCompleted && module.assignment?.expected_completion && (
                    <div className="text-white/40 text-xs">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {format(new Date(module.assignment.expected_completion), 'dd MMM', { locale: es })}
                    </div>
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};
